import { AsyncPipe } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { NewUiDashboardBirthdayRange, NewUiDashboardCandidateBirthdaysInput } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { TranslocoService } from "@jsverse/transloco";
import { derivedAsync } from "ngxtension/derived-async";
import { Avatar } from "primeng/avatar";
import { Select } from "primeng/select";
import { Skeleton } from "primeng/skeleton";
import { BehaviorSubject, catchError, map, of, switchMap } from "rxjs";
import { ImagePreloadDirective } from "../../../shared/directives/image-preload.directive";
import { AppDatePipe } from "../../../shared/pipes/date.pipe";
import { NewUiDropdownSortOnTopDirective } from "../../../shared/primeng/dropdown-sort-selected-value-on-top/new-ui-dropdown-sort-on-top.directive";
import { SettingsService } from "../../../shared/services/settings.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { CandidatesService } from "../../candidates/candidates.service";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";

export interface CandidateBirthday {
  candidateId: string;
  organizationId: string;
  name: string;
  avatarUrl: string;
  birthday: string;
}

@Component({
  selector: "new-ui-dashboard-birthdays",
  templateUrl: "./new-ui-dashboard-birthdays.component.html",
  imports: [
    RouterLink,
    TranslateDirective,
    NewUiDashboardCardComponent,
    NewUiDashboardCardHeaderComponent,
    Select,
    NewUiDropdownSortOnTopDirective,
    Avatar,
    FormsModule,
    AsyncPipe,
    AppDatePipe,
    ImagePreloadDirective,
    EmptystateComponent,
    Skeleton,
  ],
})
export class NewUiDashboardBirthdaysComponent {
  private readonly newUiDashboardService = inject(NewUiDashboardService);
  private readonly settings = inject(SettingsService);
  private readonly candidateService = inject(CandidatesService);
  private readonly transloco = inject(TranslocoService);

  protected selectedTimeframe = signal<NewUiDashboardBirthdayRange>(NewUiDashboardBirthdayRange.OneWeek);
  protected timeframeOptions = [
    {
      label: this.transloco.translate(`enum.NewUiDashboardBirthdayRange.${NewUiDashboardBirthdayRange.Today}`),
      value: NewUiDashboardBirthdayRange.Today,
    },
    {
      label: this.transloco.translate(`enum.NewUiDashboardBirthdayRange.${NewUiDashboardBirthdayRange.OneWeek}`),
      value: NewUiDashboardBirthdayRange.OneWeek,
    },
  ];

  protected candidateBirthdaysSubject = new BehaviorSubject<NewUiDashboardCandidateBirthdaysInput>({
    organizationId: this.settings.organizationId,
    timeframe: this.selectedTimeframe(),
  });

  protected candidateBirthdays$ = this.candidateBirthdaysSubject.pipe(
    switchMap((timeframe) =>
      this.newUiDashboardService.getCandidateBirthdays(timeframe).pipe(
        map((birthdays) =>
          birthdays.map((birthday) => ({
            candidateId: birthday.candidateId,
            organizationId: birthday.organizationId,
            name: `${birthday.firstName} ${birthday.lastName}`,
            avatarUrl: this.candidateService.getPictureDownloadURL(
              birthday.organizationId,
              birthday.candidateId,
              "picture"
            ),
            birthday: birthday.date,
          }))
        )
      )
    ),
    catchError(() => of(null))
  );

  protected readonly birthdays = derivedAsync(() => this.candidateBirthdays$);

  onBirthdayFilterChanged(): void {
    this.candidateBirthdaysSubject.next({
      organizationId: this.settings.organizationId,
      timeframe: this.selectedTimeframe(),
    });
  }
}
